/**
 * تبدیل تقویم میلادی ↔ شمسی (هجری خورشیدی).
 * پیاده‌سازی مستقل و بدون وابستگی، بر پایهٔ الگوریتم شمار روز.
 * همهٔ ورودی/خروجی‌ها اعداد تقویمی‌اند، نه شیء Date، تا منطق خالص و تست‌پذیر بماند.
 */
const breaks = [
    -61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 1701, 1866,
    2020, 2369, 2534, 3031, 3172, 3606, 3803, 4160, 4643, 4750, 5195, 5333,
    5346, 5576, 5661, 5798, 5860, 5900, 6403, 6404, 6406, 6672, 6673, 6675,
];
function jalCal(jy) {
    const bl = breaks.length;
    const gy = jy + 621;
    let leapJ = -14;
    let jp = breaks[0];
    if (jy < jp || jy >= breaks[bl - 1])
        throw new RangeError(`سال شمسی خارج از بازه: ${jy}`);
    let jump = 0;
    for (let i = 1; i < bl; i++) {
        const jm = breaks[i];
        jump = jm - jp;
        if (jy < jm)
            break;
        leapJ = leapJ + div(jump, 33) * 8 + div(mod(jump, 33), 4);
        jp = jm;
    }
    let n = jy - jp;
    leapJ = leapJ + div(n, 33) * 8 + div(mod(n, 33) + 3, 4);
    if (mod(jump, 33) === 4 && jump - n === 4)
        leapJ += 1;
    const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150;
    const march = 20 + leapJ - leapG;
    if (jump - n < 6)
        n = n - jump + div(jump + 4, 33) * 33;
    let leap = mod(mod(n + 1, 33) - 1, 4);
    if (leap === -1)
        leap = 4;
    return { leap, gy, march };
}
const div = (a, b) => Math.trunc(a / b);
const mod = (a, b) => a - Math.trunc(a / b) * b;
/** شمار روز میلادی → عدد جولیَن */
export function g2d(gy, gm, gd) {
    let d = div((gy + div(gm - 8, 6) + 100100) * 1461, 4) +
        div(153 * mod(gm + 9, 12) + 2, 5) +
        gd -
        34840408;
    d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752;
    return d;
}
/** عدد جولیَن → تاریخ میلادی */
export function d2g(jdn) {
    let j = 4 * jdn + 139361631;
    j = j + div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
    const i = div(mod(j, 1461), 4) * 5 + 308;
    const gd = div(mod(i, 153), 5) + 1;
    const gm = mod(div(i, 153), 12) + 1;
    const gy = div(j, 1461) - 100100 + div(8 - gm, 6);
    return [gy, gm, gd];
}
export function jalaliToGregorian(jy, jm, jd) {
    const r = jalCal(jy);
    return d2g(g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1);
}
export function gregorianToJalali(gy, gm, gd) {
    let jy = gy - 621;
    const r = jalCal(jy);
    const gdn = g2d(gy, gm, gd);
    let jdn = gdn - g2d(r.gy, 3, r.march);
    if (jdn >= 0) {
        if (jdn <= 185)
            return [jy, 1 + div(jdn, 31), mod(jdn, 31) + 1];
        jdn -= 186;
    }
    else {
        jy -= 1;
        jdn += 179;
        if (r.leap === 1)
            jdn += 1;
    }
    return [jy, 7 + div(jdn, 30), mod(jdn, 30) + 1];
}
export function isJalaliLeap(jy) {
    return jalCal(jy).leap === 0;
}
export function jalaliMonthLength(jy, jm) {
    if (jm <= 6)
        return 31;
    if (jm <= 11)
        return 30;
    return isJalaliLeap(jy) ? 30 : 29;
}
export const MONTHS = [
    "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
    "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند",
];
/** روزهای هفته با شنبه به‌عنوان روز اول */
export const WEEKDAYS = ["شنبه", "یک‌شنبه", "دوشنبه", "سه‌شنبه", "چهارشنبه", "پنج‌شنبه", "جمعه"];
export const WEEKDAYS_SHORT = ["ش", "ی", "د", "س", "چ", "پ", "ج"];
/** ۰ = شنبه … ۶ = جمعه */
export function weekdayIndex(iso) {
    const [y, m, d] = iso.split("-").map(Number);
    const js = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // ۰ = یک‌شنبه
    return (js + 1) % 7;
}
const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
export const toFa = (v) => String(v).replace(/\d/g, (d) => FA_DIGITS[Number(d)]);
/** "۲۹ مرداد ۱۴۰۵" */
export function formatJalali(iso, opts = {}) {
    const [gy, gm, gd] = iso.split("-").map(Number);
    const [jy, jm, jd] = gregorianToJalali(gy, gm, gd);
    const core = `${toFa(jd)} ${MONTHS[jm - 1]}${opts.withYear === false ? "" : " " + toFa(jy)}`;
    return opts.withWeekday ? `${WEEKDAYS[weekdayIndex(iso)]} ${core}` : core;
}
/** "۱۴۰۵/۰۵/۲۹" */
export function formatJalaliShort(iso) {
    const [gy, gm, gd] = iso.split("-").map(Number);
    const [jy, jm, jd] = gregorianToJalali(gy, gm, gd);
    return toFa(`${jy}/${String(jm).padStart(2, "0")}/${String(jd).padStart(2, "0")}`);
}
//# sourceMappingURL=jalali.js.map